import React, { useState, useRef } from "react";
import {
    Table,
    Input,
    Button,
    Space,
    Spin,
    Modal,
    Form,
    Select,
    DatePicker,
} from "antd";
import { message } from "../../components/antdMessageBridge";
import CommonObj from "../../CommonObj";
import CalendarIcon from "../../assets/img/svg/xCalendaricon.svg?react";
import { getUTCDate } from "../../components/utcDate";
import {
    SearchOutlined,
} from "@ant-design/icons";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { useAuth } from "../../auth/AuthContext";
import "./reports.css";
import { exportToExcel } from '../exporttoexcel/exporttoexcel';
import ReportModal from './report-modal';
import configJson from "../../Config.json";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import { renderCommonPaginationTotal } from "../../components/commonpagination/common-pagination";
import "../../components/commonpagination/common-pagination.css";
import { buildTimestampedFileName } from "../../components/cstDateFileName";

const { Option } = Select;
const { TextArea } = Input;

const BookDetailsReport = () => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const [totalRow, setTotalRow] = useState(1);
    const [searchData, setSearchData] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [searchFilters, setsearchFilters] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [startTime, setStartTime] = useState("");
    const [rejectionOptions, setRejectOptions] = useState([]);
    const [reasonLoading, setReasonLoading] = useState(false);
    const { roleUserId } = useAuth();
    const { accessToken, isAuthenticated } = useAuthToken();

    const [filteredData, setFilteredData] = useState([]);

    const [isVisible, setIsVisible] = useState(true);

    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [startfloatLabel, setStartfloatLabel] = useState('');
    const [endfloatLabel, setEndfloatLabel] = useState('');
    const [errors, setErrors] = useState({});
    const searchInput = useRef(null);
    const [reportModalVisible, setReportModalVisible] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalBody, setModalBody] = useState("");

    const handleReset = (dataIndex, confirm, clearFilters) => {
        clearFilters();
        setsearchFilters((prev) => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        fetchData({
            pagination,
            sorter: sortedInfo,
            filters: { ...searchFilters, [dataIndex]: undefined },
        });
    };

    const handleExport_new = async () => {
        if (!validate()) {
            console.log("Validation Failed:", errors);
            return;
        }
        const startdate = selectedStartDate ? selectedStartDate.format('YYYY-MM-DD') : null;
        const enddate = selectedEndDate ? selectedEndDate.format('YYYY-MM-DD') : null;

        const payload = {
            "startDate": startdate,
            "endDate": enddate,
            "reportID": 9,
            "userIndex": roleUserId,
            "userTimeZone": "string",
            "operatorID": "string",
            "operatorName": "string",
            "qcRework": "string",
            "qcStatus": "string",
            pageNo: 1,
            pageSize: -1,
            "search": "string",
            "orderByColumn": "string",
            "orderBy": "string",
            "reportName": "Book Detail Report"

        };

        setLoading(true);
        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const response = await fetch(CommonObj.Endpoints.GetReportData, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error("Network response was not ok");



            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
            } catch (e) {
                if (responseText.includes("No books found.")) {
                    result = [];
                } else {
                    throw new Error(responseText || "Failed to parse API response.");
                }
            }

            const books = Array.isArray(result) ? result : [result];
            const reportRows = books[0]?.bookDetailReport || [];
            if (reportRows.length === 0) {
                message.error("The grid is currently empty. There are no records to export.");
                setData([]);
                setPagination((prev) => ({
                    ...prev,
                    current: 1,
                    total: 0,
                }));
                setFilteredData([]);
                setTotalRow(0);
                return;
            }
            if (reportRows[0].reportDirectDownload && reportRows[0].reportMessage?.trim()) {
                setModalTitle("Report Processing Initiated");
                setModalBody(reportRows[0].reportMessage);
                setReportModalVisible(true);
            }
            else {
                const dynamicFileName = buildTimestampedFileName("BookDetailsReport");
                exportToExcel(reportRows, columns, dynamicFileName);
            }
        } catch (err) {
            setError(err);
            message.error("Failed to export data. Please try again.");
        } finally {
            setLoading(false);
        }

    };

    const validate = () => {
        const newErrors = {};

        if (!selectedStartDate) {
            newErrors.startdate = "Please select start date.";
        }
        if (!selectedEndDate) {
            newErrors.enddate = "Please select end date.";
        }

        if (selectedStartDate && selectedEndDate && new Date(selectedEndDate) < new Date(selectedStartDate)) {
            newErrors.datevalidate = "End Date cannot be lesser than Start Date";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleStartDateChange = (date) => {

        setSelectedStartDate(date);

        if (date !== null) {
            setStartfloatLabel('float-label');
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.startdate;
                return newErrors;
            });
        } else {
            setStartfloatLabel('');
        }
    };

    const disableFutureDates = (current) => {
        const today = new Date();
        return current && current > today;
    };

    const handleEndDateChange = (date) => {

        setSelectedEndDate(date);

        if (date !== null) {
            setEndfloatLabel('float-label');
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.enddate;
                return newErrors;
            });
        } else {
            setEndfloatLabel('');
        }
    };

    const handleCleardate = () => {
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setStartfloatLabel('');
        setEndfloatLabel('');

        setData([]);
        setFilteredData([]);
        setTotalRow(0);

        setPagination((prev) => ({
            ...prev,
            current: 1,
            total: 0,
        }));

        setErrors({});
    };
    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
        }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    style={{ marginBottom: 8, display: "block" }}
                    onPressEnter={() => confirm()}
                />
                <Space>
                    <Button type="primary" size="small" onClick={() => confirm()}>
                        Search
                    </Button>
                    <Button
                        size="small"
                        onClick={() => {
                            handleReset(dataIndex, confirm, clearFilters);
                            confirm(); // Explicitly call confirm to refresh the table
                        }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
        ),
        filteredValue: searchFilters[dataIndex] ? [searchFilters[dataIndex]] : null,
        onFilter: (value, record) =>
            record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current.select(), 100);
            }
        },
        sorter: true,
        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null,
    });

    const handleCancel = () => {
        setIsModalVisible(false);
        setSelectedRecord(null);
        form.resetFields();
    };

    const fetchRejectionData = async () => {
        setLoading(true);
        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const response = await fetch(CommonObj.Endpoints.GetRejectionTypes, {
                method: "POST",
                headers: myHeaders,
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const result = await response.json();
            const books = Array.isArray(result) ? result : [result];
            const formattedData = books.map((item) => ({
                ...item,
                key: item.referenceId,
            }));
            setRejectOptions(formattedData);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const showRejectModal = (record) => {
        const commaSeparatedIds = selectedRowKeys?.join(",");
        fetchRejectionData();
        setSelectedRecord(record);
        setIsModalVisible(true);
    };





    const handleManifestReject = async (key) => {
        const commaSeparatedIds = selectedRowKeys.join(",");
        let bids;
        if (commaSeparatedIds) {
            bids = commaSeparatedIds;
        } else {
            bids = selectedRecord.bidNo;
        }

        try {
            const loadingKey = "rejecting";
            message.loading({ content: "Rejecting BID...", key: loadingKey });
            const endUTCTime = getUTCDate();
            const payload = {
                biDs: bids,
                userId: roleUserId,
                startTime: startTime,
                endTime: endUTCTime?.UTCDate,
                rejectionType: key.rejectionType,
                rejectionReason: key.rejectionReason,
            };

            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            // Replace with your actual API call using axios
            const response = await fetch(CommonObj.Endpoints.RejectManifest, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            if (response) {
                fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });

                const newData = data.map((item) => {
                    if (item.key === selectedRecord.key) {
                        return { ...item, status: "Rejected" };
                    }
                    return item;
                });
                const startUTCTime = getUTCDate();
                setStartTime(startUTCTime?.UTCDate);
                message.success({
                    content: "BID Rejected Successfully!",
                    key: loadingKey,
                    duration: 2,
                });
                handleCancel();
                setSelectedRowKeys();
            } else {
                message.error({
                    content: "Failed to Reject BID.",
                    key: loadingKey,
                    duration: 2,
                });
            }
        } catch (error) {
            console.error("API Error:", error);
            message.error({
                content: "An error occurred. Please try again.",
                key: "rejecting",
                duration: 2,
            });
        }
    };

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };
    const handleManifestAccept = async (key) => {
        try {
            let bids;
            // Show a loading message while the API call is in progress
            if (Array.isArray(key)) {
                bids = key.length > 1 ? key.join(",") : key[0];
            } else {
                bids = key;
            }
            if (!bids) {
                message.error("No rows selected.");
                return;
            }

            if (key.length > 0) {
                const loadingMessageKey = "loading";
                message.loading({
                    content: "Updating status...",
                    key: loadingMessageKey,
                });
                const endUTCTime = getUTCDate();
                const payload = {
                    bids: bids,
                    userId: roleUserId,
                    startTime: startTime,
                    endTime: endUTCTime?.UTCDate,
                };
                const myHeaders = {
                    Authorization: `Bearer ${accessToken}`,
                    "X-CSRF-Token": encryptTimestamp(),
                    "Content-Type": "application/json",
                };

                const response = await fetch(CommonObj.Endpoints.AcceptManifest, {
                    method: "POST",
                    headers: myHeaders,
                    body: JSON.stringify(payload),
                });
                if (response) {
                    fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });
                    message.success({
                        content: `Status updated to Manifest Accepted!`,
                        key: loadingMessageKey,
                        duration: 2,
                    });
                } else {
                    // Handle API-specific errors
                    message.error({
                        content: `Failed to update status: ${response.data.message}`,
                        key: loadingMessageKey,
                        duration: 2,
                    });
                }
            }
            // Replace with your actual API call using axios
        } catch (error) {
            console.error("API Error:", error);
            message.error({
                content: "An error occurred. Please try again.",
                key: "loading",
                duration: 2,
            });
        }
    };

    const columns = [
        {
            title: "Date",
            dataIndex: "completedDate",
            key: "completedDate",
            //sorter: (a, b) => a.completedDate.localeCompare(b.completedDate),
            //...getColumnSearchProps("completedDate"),
        },
        {
            title: "Intake",
            dataIndex: "intake",
            key: "intake",
            //sorter: (a, b) => a.shelfID.localeCompare(b.shelfID),
            //...getColumnSearchProps("shelfID"),
        },
        {
            title: "Cutting",
            dataIndex: "cutting",
            key: "cutting",
            //sorter: (a, b) => a.bidNo.localeCompare(b.bidNo),
            //...getColumnSearchProps("bidNo"),
        },
        {
            title: "Scanning",
            dataIndex: "scanning",
            key: "scanning",
            //sorter: (a, b) => a.weight.localeCompare(b.weight),
            //...getColumnSearchProps("weight"),
        },
        {
            title: "QC",
            dataIndex: "qc",
            key: "qc",
            //sorter: (a, b) => a.weight.localeCompare(b.weight),
            //...getColumnSearchProps("weight"),
        },
        //{
        //    title: "Delivery To S3",
        //    dataIndex: "deliveryToS3",
        //    key: "deliveryToS3",
        //    //sorter: (a, b) => a.weight.localeCompare(b.weight),
        //    //...getColumnSearchProps("weight"),
        //},
        {
            title: "Client Verification",
            dataIndex: "client",
            key: "client",
            //sorter: (a, b) => a.weight.localeCompare(b.weight),
            //...getColumnSearchProps("weight"),
        },
        //{
        //    title: "Ready for Recycle",
        //    dataIndex: "readyforDelivery",
        //    key: "readyforDelivery",
        //    //sorter: (a, b) => a.weight.localeCompare(b.weight),
        //    //...getColumnSearchProps("weight"),
        //},
        {
            title: "Recycle",
            dataIndex: "recycle",
            key: "recycle",
            //sorter: (a, b) => a.weight.localeCompare(b.weight),
            //...getColumnSearchProps("weight"),
        },
        //{
        //    title: "Empty Rack Scan",
        //    dataIndex: "emptyRackScan",
        //    key: "emptyRackScan",
        //    //sorter: (a, b) => a.weight.localeCompare(b.weight),
        //    //...getColumnSearchProps("weight"),
        //},
        {
            title: "Total Records",
            dataIndex: "totalRecords",
            key: "totalRecords",
            //sorter: (a, b) => a.weight.localeCompare(b.weight),
            //...getColumnSearchProps("weight"),
        },

    ];

    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        const normalizeFilters = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] && filters[key].length > 0) {
                normalizeFilters[key] = filters[key][0];
            }
        });

        setsearchFilters(normalizeFilters);
        setSortedInfo(sorter);

        // Keep pagination fully controlled
        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        // Fetch using the new pagination
        fetchData({
            pagination: {
                current: newPagination.current,
                pageSize: newPagination.pageSize,
            },
            sorter,
            filters: normalizeFilters,
        });
    };


    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        preserveSelectedRowKeys: true,
        getCheckboxProps: (record) => ({
            disabled: record.status === "Manifest Reject",
            // Optional: disable selection for rows with empty rackno
            // disabled: !record.rackno,
        }),
    };

    const formatTime = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${hours}:${minutes}`;





    };

    const fetchData = async ({ pagination: pag, filters, sorter }) => {

        const startdate = selectedStartDate ? selectedStartDate.format('YYYY-MM-DD') : null;
        const enddate = selectedEndDate ? selectedEndDate.format('YYYY-MM-DD') : null;

        const payload = {
            "startDate": startdate,//"2025-10-01 08:32:17.177",
            "endDate": enddate,
            "reportID": 9,
            "userIndex": roleUserId,
            "userTimeZone": "string",
            "operatorID": "string",
            "operatorName": "string",
            "qcRework": "string",
            "qcStatus": "string",
            pageNo: pag?.current,
            pageSize: pag?.pageSize || 10,
            "search": "string",
            "orderByColumn": "string",
            "orderBy": "string",
            "reportName": "Book Detail Report"
        };

        setLoading(true);
        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const response = await fetch(CommonObj.Endpoints.GetReportData, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error("Network response was not ok");

            const responseText = await response.text();
            let result;

            // Try to parse the response as JSON.
            try {
                result = JSON.parse(responseText);
            } catch (e) {
                // If parsing fails, it's a non-JSON response.
                // Check if the response text indicates "no books found".
                if (responseText.includes("No books found.")) {
                    // Treat this as an empty result.
                    result = [];
                } else {
                    // For any other non-JSON response, throw an error.
                    throw new Error(responseText || "Failed to parse API response.");
                }
            }
            const books = Array.isArray(result) ? result : [result];
            const formattedData = books[0]?.bookDetailReport || [];

            if (formattedData.length === 0) {
                message.warning("No data found in selected date range");
            }

            setData(formattedData);

            const total = formattedData[0]?.totalRows || 0;
            setTotalRow(total);
            setPagination((prev) => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));

            const startUTCTime = getUTCDate();
            setStartTime(startUTCTime?.UTCDate);

            setFilteredData(formattedData);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Spin size="large" style={{ margin: "20px auto" }} />
        );
    }



    return (
        <div className="active-page-container">
            {/* PopUp Modal */}
            <ReportModal title={modalTitle} modalBody={modalBody} Visible={reportModalVisible} setVisible={setReportModalVisible} queueURL="reportsdownloadqueue" />

            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>Book Details Report</h4>
                </div>
                <div className="active-page-header-action-container">
                    <div className="active-page-header-button-container">



                        <button className="active-page-header-button" onClick={() => setIsVisible(!isVisible)}>

                            <span>
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M16.5 4.36133V4.87869C16.5 5.65707 16.5 6.04626 16.3053 6.3689C16.1106 6.69153 15.7549 6.89177 15.0435 7.29224L12.8587 8.52212C12.3814 8.79081 12.1427 8.92516 11.9719 9.07352C11.616 9.38245 11.3969 9.74547 11.2977 10.1907C11.25 10.4045 11.25 10.6547 11.25 11.155V13.1571C11.25 14.5892 11.25 15.3053 10.749 15.6186C10.2481 15.932 9.54359 15.6566 8.13462 15.1058C7.46346 14.8434 7.12789 14.7122 6.93894 14.4463C6.75 14.1804 6.75 13.8393 6.75 13.1571L6.75 11.155C6.75 10.6547 6.75 10.4045 6.70233 10.1907C6.60306 9.74547 6.38399 9.38245 6.02813 9.07351C5.85725 8.92516 5.6186 8.79081 5.14128 8.52212L2.95651 7.29224C2.2451 6.89177 1.8894 6.69153 1.6947 6.3689C1.5 6.04626 1.5 5.65707 1.5 4.87869V4.36133"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                    />
                                    <path
                                        opacity="0.5"
                                        d="M16.5 4.361C16.5 3.36586 16.5 2.8683 16.1705 2.55915C15.841 2.25 15.3107 2.25 14.25 2.25H3.75C2.68934 2.25 2.15901 2.25 1.8295 2.55915C1.5 2.8683 1.5 3.36586 1.5 4.361"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                    />
                                </svg>
                            </span>
                        </button>
                    </div>
                    <div className="active-page-header-button-container">
                        <button className="active-page-header-button" data-track="reports-book-details-export-csv" onClick={handleExport_new} >
                            <span>
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_875_15157)">
                                        <path
                                            d="M11.5447 3.04024L11.1433 3.48621L11.1433 3.48621L11.5447 3.04024ZM14.5138 5.71241L14.1124 6.15839L14.5138 5.71241ZM16.2405 7.61557L15.6924 7.85967L15.6924 7.85968L16.2405 7.61557ZM2.37868 15.6213L2.80294 15.1971H2.80294L2.37868 15.6213ZM15.6213 15.6213L15.1971 15.1971L15.6213 15.6213ZM10.5 16.5V15.9H7.5V16.5V17.1H10.5V16.5ZM1.5 10.5H2.1V7.5H1.5H0.9V10.5H1.5ZM16.5 10.1722H15.9V10.5H16.5H17.1V10.1722H16.5ZM11.5447 3.04024L11.1433 3.48621L14.1124 6.15839L14.5138 5.71241L14.9152 5.26643L11.9461 2.59426L11.5447 3.04024ZM16.5 10.1722H17.1C17.1 8.90922 17.1121 8.09787 16.7886 7.37147L16.2405 7.61557L15.6924 7.85968C15.8879 8.29862 15.9 8.80322 15.9 10.1722H16.5ZM14.5138 5.71241L14.1124 6.15839C15.1299 7.07417 15.4969 7.42073 15.6924 7.85967L16.2405 7.61557L16.7886 7.37147C16.4651 6.64507 15.8539 6.11131 14.9152 5.26643L14.5138 5.71241ZM7.52234 1.5V2.1C8.71153 2.1 9.15094 2.10926 9.54196 2.25931L9.75692 1.69914L9.97188 1.13897C9.325 0.890737 8.62066 0.9 7.52234 0.9V1.5ZM11.5447 3.04024L11.9461 2.59426C11.1337 1.8631 10.6187 1.38716 9.97188 1.13897L9.75692 1.69914L9.54196 2.25931C9.93306 2.40939 10.2639 2.69476 11.1433 3.48621L11.5447 3.04024ZM7.5 16.5V15.9C6.06882 15.9 5.0558 15.8987 4.28813 15.7955C3.53772 15.6946 3.11222 15.5063 2.80294 15.1971L2.37868 15.6213L1.95442 16.0456C2.52382 16.615 3.24476 16.866 4.12824 16.9848C4.99445 17.1013 6.10275 17.1 7.5 17.1V16.5ZM1.5 10.5H0.9C0.9 11.8973 0.898726 13.0055 1.01519 13.8718C1.13397 14.7552 1.38501 15.4762 1.95442 16.0456L2.37868 15.6213L2.80294 15.1971C2.49367 14.8878 2.30537 14.4623 2.20448 13.7119C2.10127 12.9442 2.1 11.9312 2.1 10.5H1.5ZM10.5 16.5V17.1C11.8973 17.1 13.0055 17.1013 13.8718 16.9848C14.7552 16.866 15.4762 16.615 16.0456 16.0456L15.6213 15.6213L15.1971 15.1971C14.8878 15.5063 14.4623 15.6946 13.7119 15.7955C12.9442 15.8987 11.9312 15.9 10.5 15.9V16.5ZM16.5 10.5H15.9C15.9 11.9312 15.8987 12.9442 15.7955 13.7119C15.6946 14.4623 15.5063 14.8878 15.1971 15.1971L15.6213 15.6213L16.0456 16.0456C16.615 15.4762 16.866 14.7552 16.9848 13.8718C17.1013 13.0055 17.1 11.8973 17.1 10.5H16.5ZM1.5 7.5H2.1C2.1 6.06882 2.10127 5.0558 2.20448 4.28813C2.30537 3.53772 2.49367 3.11222 2.80294 2.80294L2.37868 2.37868L1.95442 1.95442C1.38501 2.52382 1.13397 3.24476 1.01519 4.12824C0.898726 4.99445 0.9 6.10275 0.9 7.5H1.5ZM7.52234 1.5V0.9C6.11759 0.9 5.0039 0.898736 4.13418 1.01514C3.24765 1.13379 2.52428 1.38455 1.95442 1.95442L2.37868 2.37868L2.80294 2.80294C3.11175 2.49413 3.53856 2.30555 4.29336 2.20453C5.06497 2.10126 6.08378 2.1 7.52234 2.1V1.5Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M9.75 1.875V3.75C9.75 5.51777 9.75 6.40165 10.2992 6.95083C10.8483 7.5 11.7322 7.5 13.5 7.5H16.5"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M6.375 10.125L6.375 13.875M6.375 13.875L7.875 12.4688M6.375 13.875L4.875 12.4688"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_875_15157">
                                            <rect width="18" height="18" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </span>
                        </button>
                    </div>

                </div>
            </div>
            <div className="advance-filter-container">
                <div className="advance-filter-title-container">
                    <div className="advance-filter-title">
                        <span>
                            <svg
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M14.7245 3.89258V4.35423C14.7245 5.04879 14.7245 5.39607 14.5508 5.68396C14.3771 5.97184 14.0597 6.15052 13.4249 6.50787L11.4754 7.6053C11.0494 7.84506 10.8365 7.96494 10.684 8.09732C10.3665 8.37299 10.171 8.69691 10.0824 9.0942C10.0399 9.28498 10.0399 9.50821 10.0399 9.95468V11.7412C10.0399 13.0191 10.0399 13.658 9.59287 13.9376C9.14585 14.2172 8.51723 13.9714 7.25999 13.48C6.66111 13.2458 6.36167 13.1288 6.19308 12.8915C6.02448 12.6542 6.02448 12.3499 6.02448 11.7412L6.02448 9.95468C6.02448 9.50821 6.02448 9.28498 5.98194 9.0942C5.89336 8.69691 5.69788 8.37299 5.38035 8.09732C5.22787 7.96494 5.01491 7.84506 4.589 7.6053L2.63951 6.50787C2.00471 6.15052 1.68731 5.97184 1.51358 5.68396C1.33984 5.39607 1.33984 5.04879 1.33984 4.35423V3.89258"
                                    stroke="#0009E2"
                                    strokeWidth="1.2"
                                />
                                <path
                                    opacity="0.5"
                                    d="M14.7245 3.89148C14.7245 3.00351 14.7245 2.55953 14.4305 2.28367C14.1365 2.00781 13.6633 2.00781 12.7168 2.00781H3.34754C2.40111 2.00781 1.92789 2.00781 1.63386 2.28367C1.33984 2.55953 1.33984 3.00351 1.33984 3.89148"
                                    stroke="#009DDB"
                                    strokeWidth="1.2"
                                />
                            </svg></span>Select Date Range</div>
                    <div className="advance-filter-action">
                        <button style={{ display: "none" }} className="active-page-header-button">
                            <span>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        opacity="0.5"
                                        cx="11.5"
                                        cy="11.5"
                                        r="7.5"
                                        stroke="currentColor"
                                        strokeWidth="1.35"
                                    />
                                    <path
                                        d="M15 12H8"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                        </button>
                        <button
                            style={{ display: "none" }} className="active-page-header-button">
                            <span>
                                <svg
                                    width="25"
                                    height="25"
                                    viewBox="0 0 25 25"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="12.0311"
                                        cy="11.9999"
                                        r="7.51943"
                                        stroke="#E93548"
                                    />
                                    <path
                                        d="M13.912 10.1192L10.1523 13.8789M10.1523 10.1191L13.912 13.8788"
                                        stroke="#E93548"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
                {isVisible && (
                    <div className="advance-filter-body-container">

                        <div className="advance-filter-input-container">

                            <div className="advance-filter-input-width">
                                <div className="field-group">
                                    <DatePicker variant="underlined" placeholder=""
                                        className={`${"book-page-form-input-text"} ${selectedStartDate === null ? "" : "has-text"}`}
                                        suffixIcon={<CalendarIcon />}
                                        value={selectedStartDate}
                                        onChange={handleStartDateChange}
                                        disabledDate={disableFutureDates}
                                        allowClear={false} id="startdate-report-1"
                                    />
                                    <label className={startfloatLabel} htmlFor="startdate-report-1">
                                        Start Date <span className="form-required">*</span>
                                    </label>
                                    {errors.startdate && (
                                        <span className="error-msg">{errors.startdate}</span>
                                    )}
                                    {errors.datevalidate && (
                                        <span className="error-msg-1">{errors.datevalidate}</span>
                                    )}
                                </div>
                            </div>
                            <div className="advance-filter-input-width">
                                <div className="field-group">
                                    <DatePicker variant="underlined" placeholder=""
                                        className={`${"book-page-form-input-text"} ${selectedEndDate === null ? "" : "has-text"}`}
                                        suffixIcon={<CalendarIcon />}
                                        value={selectedEndDate}
                                        onChange={handleEndDateChange}
                                        disabledDate={disableFutureDates}
                                        allowClear={false} id="enddate-report-1"
                                    />
                                    <label htmlFor="enddate-report-1" className={endfloatLabel}>
                                        End Date <span className="form-required">*</span>
                                    </label>
                                    {errors.enddate && (
                                        <span className="error-msg">{errors.enddate}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="advance-filter-button-container">
                            <button className="cancel-button-gray-outline" onClick={handleCleardate}>Clear</button>
                            {/*<button class="submit-button-gradiant-outline" onClick={() => fetchData({ pagination, sorter: sortedInfo, filters: searchFilters })}>Generate</button>*/}
                            <button className="submit-button-gradiant-outline" data-track="bookdetails-report-generate" onClick={(e) => {

                                e.preventDefault();
                                if (!validate()) {
                                    const firstErrorField = Object.keys(errors)[0];
                                    if (firstErrorField) {
                                        const element = document.getElementById(firstErrorField);
                                        if (element) element.focus();
                                    }
                                    return;
                                }

                                const resetPagination = {
                                    ...pagination,
                                    current: 1
                                };
                                setPagination(resetPagination);
                                fetchData({
                                    pagination: resetPagination,
                                    sorter: sortedInfo,
                                    filters: searchFilters
                                });
                            }}>Generate</button>

                        </div>

                    </div>
                )}
            </div>
            <div className="active-page-body-container">
                <div className="rounded-table-wrapper">
                    <Table

                        columns={columns}
                        dataSource={data}
                        className="common-pagination common-grid"
                        pagination={{
                            //showTotal={(totalRow, range) => `${range[0]}-${range[1]} of ${totalRow} items`},
                            ...pagination,
                            pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                            showSizeChanger: true,
                            position: ["bottomRight"],
                            //total: totalRow,
                            defaultPageSize: 10,
                            showTotal: (total, range) =>
                                renderCommonPaginationTotal(total, range, {
                                    pagination,
                                    handleTableChange,
                                    searchFilters,
                                    sortedInfo
                                }),
                        }}
                        rowKey={(record) => record.bidNo}
                        onChange={handleTableChange}
                        scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
                        rowClassName={getRowClassName}
                    />
                </div>
                <Modal
                    title={`Manifest Rejection`}
                    open={isModalVisible}
                    onCancel={handleCancel}
                    footer={null} // Remove default footer to use form's submit button
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleManifestReject}
                        initialValues={{ bidId: selectedRecord?.bidId }}
                    >
                        <Form.Item
                            name="bidId"
                            label="Bid ID"
                            hidden // This field can be hidden since the user doesn't need to change it
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="rejectionType"
                            label="Rejection Type"
                            rules={[
                                { required: true, message: "Please select a rejection type!" },
                            ]}
                        >

                            <Select
                                placeholder="Select a reason type"
                                loading={reasonLoading} // Use the loading state here
                            >
                                {rejectionOptions.map((option) => (
                                    <Option key={option.id} value={option.id}>
                                        {option.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="rejectionReason"
                            label="Rejection Reason"
                            rules={[{ required: true, message: "Please provide a reason!" }]}
                        >
                            <TextArea rows={4} />
                        </Form.Item>
                        <Form.Item className="model-action-container">
                            <Space>
                                <button
                                    className="ant-btn ant-btn-default slider-clear-button"
                                    type="button"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="ant-btn ant-btn-primary accept-button"
                                    type="submit"
                                >
                                    Submit
                                </button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>

                {/* <Button>Accept</Button>
            <Button>Reject</Button> */}
            </div>

        </div>
    );
};
export default BookDetailsReport;